export const AIRCRAFT_STATES = [
  /** Inbound, under player control, drawing a path is allowed. */
  "flying",
  /** Committed to a runway, rolling from the approach gate to the end. */
  "landing",
  /** Taxiing from the runway end to the apron. */
  "taxiing-in",
  /** Taxiing back out to the approach gate. */
  "taxiing-out",
  /** Waiting at the gate for its departure slot. */
  "holding",
  /** Rolling down the runway and climbing out. */
  "departing",
  /** Stationary on the apron. */
  "parked",
] as const;

export type AircraftState = (typeof AIRCRAFT_STATES)[number];

/** States where the aircraft is on the ground and cannot collide. */
export const GROUND_STATES: ReadonlySet<AircraftState> = new Set([
  "taxiing-in",
  "taxiing-out",
  "holding",
  "parked",
]);

/** States that count as outbound traffic and use the outbound livery. */
export const OUTBOUND_STATES: ReadonlySet<AircraftState> = new Set([
  "taxiing-out",
  "holding",
  "departing",
]);

/**
 * States that hold a runway against a fresh landing clearance.
 *
 * `flying` counts because a clearance is a claim, not a request: the aircraft
 * holding it will arrive whether or not a second one is pointed at the same
 * strip, and nothing downstream sequences the two. `landing` and `departing`
 * are the roll itself. The taxi out and the wait at the gate are deliberately
 * absent — every landed aircraft queues to depart from the runway it used, so
 * counting those would leave a runway permanently claimed by its own queue.
 */
export const RUNWAY_CLAIM_STATES: ReadonlySet<AircraftState> = new Set([
  "flying",
  "landing",
  "departing",
]);

export function isOnGround(state: AircraftState): boolean {
  return GROUND_STATES.has(state);
}

export function isOutbound(state: AircraftState): boolean {
  return OUTBOUND_STATES.has(state);
}

export function claimsRunway(state: AircraftState): boolean {
  return RUNWAY_CLAIM_STATES.has(state);
}
